import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api/client';
import { useAuth } from './AuthContext';

type FeatureFlags = {
    orderingEnabled: boolean;
    paymentEnabled: boolean;
    multiLanguageEnabled: boolean;
    brandingEnabled: boolean;
    deliveryEnabled: boolean;
    deliveryPaymentEnabled: boolean;
    deliveryGuestEnabled: boolean;
    browseMenuEnabled: boolean;
    cashflowEnabled: boolean;
    posEnabled: boolean;
    printCenterEnabled: boolean;
    tableServiceEnabled: boolean;
    orderBoardEnabled: boolean;
};

const defaultFlags: FeatureFlags = {
    orderingEnabled: false,
    paymentEnabled: false,
    multiLanguageEnabled: false,
    brandingEnabled: false,
    deliveryEnabled: false,
    deliveryPaymentEnabled: false,
    deliveryGuestEnabled: false,
    browseMenuEnabled: false,
    cashflowEnabled: false,
    posEnabled: false,
    printCenterEnabled: false,
    tableServiceEnabled: false,
    orderBoardEnabled: false,
};

type FeatureContextValue = {
    features: FeatureFlags;
    grants: FeatureFlags;
    loading: boolean;
    error: string | null;
    refresh: () => void;
};

const FeatureContext = createContext<FeatureContextValue | undefined>(undefined);

export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [features, setFeatures] = useState<FeatureFlags>(defaultFlags);
    const [grants, setGrants] = useState<FeatureFlags>(defaultFlags);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        let cancelled = false;

        async function loadFeatures() {
            if (!isAuthenticated) {
                setFeatures(defaultFlags);
                setGrants(defaultFlags);
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const response = await apiFetch('/api/tenant/self/features');
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || 'Impossibile recuperare le funzionalità del tenant');
                }
                const payload = await response.json();
                if (!cancelled) {
                    const { grants: payloadGrants, ...effective } = payload || {};
                    setFeatures({ ...defaultFlags, ...effective });
                    setGrants({ ...defaultFlags, ...effective, ...(payloadGrants || {}) });
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : 'Errore nel recupero delle funzionalità';
                    setError(message);
                    setFeatures(defaultFlags);
                    setGrants(defaultFlags);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadFeatures();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, reloadToken]);

    const value = useMemo<FeatureContextValue>(() => ({
        features,
        grants,
        loading,
        error,
        refresh: () => setReloadToken((token) => token + 1),
    }), [features, grants, loading, error]);

    return (
        <FeatureContext.Provider value={value}>
            {children}
        </FeatureContext.Provider>
    );
};

export const useFeatures = () => {
    const context = useContext(FeatureContext);
    if (!context) {
        throw new Error('useFeatures must be used within a FeatureProvider');
    }
    return context;
};

export type { FeatureFlags };
